<template>
  <div class="oring-assistant-upper-grid-wrapper">
    <Grid
      :table-columns="tableColumns"
      :data-items="dataItems"
      disable-paging
      class="grid-table"
      :scrollable="'none'"
    >
      <template #dimensionCell="{props: cellProps}">
        <td :data-th="getColumnTitle(cellProps.field)">
          <div class="td-content">
            <span>{{ cellProps.dataItem[cellProps.field] }}</span>
            <span class="td-content__unit-spacing">{{ isInchComputed }}</span>
          </div>
        </td>
      </template>
      <template #stretching="{props: cellProps}">
        <td :data-th="getColumnTitle(cellProps.field)">
          <div class="td-content-stretching">
            <div class="td-content-stretching__values">
              <span
                v-if="cellProps.field !== 'fillingRatio' && hasMinField(cellProps.field)"
                :class="getColorClass(cellProps.dataItem[cellProps.field + 'MinColorRating'])"
              >
                <span class="value-label">{{ i18n.tv('Frontend.Parameter.MinValue', 'ORingConfiguratorImport') }}</span>
                {{ cellProps.dataItem[cellProps.field + 'Min'] }}%
              </span>
              <span
                v-if="cellProps.field === 'fillingRatio' && hasMeanField(cellProps.field)"
                :class="getColorClass(cellProps.dataItem[cellProps.field + 'MeanColorRating'])"
              >
                <span class="value-label">Ø</span>
                {{ cellProps.dataItem[cellProps.field + 'Mean'] }}%
              </span>
              <span :class="getColorClass(cellProps.dataItem[cellProps.field + 'MaxColorRating'])">
                <span class="value-label">max.</span>
                {{ cellProps.dataItem[cellProps.field + 'Max'] }}%
              </span>
            </div>
          </div>
        </td>
      </template>
      <template #simpleCell="{props: cellProps}">
        <td :data-th="getColumnTitle(cellProps.field)">
          {{ cellProps.dataItem[cellProps.field] }}
        </td>
      </template>
      <template #materialCell="{props: cellProps}">
        <td :data-th="getColumnTitle(cellProps.field)">
          <div class="material-list">
            <ul>
              <li
                v-for="(mat, idx) in visibleMaterials(cellProps.dataItem[cellProps.field])"
                :key="idx"
              >
                {{ mat }}
              </li>
            </ul>
            <span
              v-if="cellProps.dataItem[cellProps.field]?.length > 4"
              class="material-list__toggle"
              @click="materialsExpanded = !materialsExpanded"
            >
              {{ materialsExpanded ? i18n.tv('show-less', 'product') : i18n.tv('show-more', 'product') }}
              <NavigationExpandMore
                :class="{
                  'expand-more-icon': !materialsExpanded,
                  'expand-less-icon': materialsExpanded
                }"
              />
            </span>
          </div>
        </td>
      </template>
    </Grid>
  </div>
</template>
<script setup lang="ts">
import {ref} from 'vue'
import Grid from '~/components/grid/Grid'
import useI18nExtended from '~/composables/usei18nExtended'
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'

const i18n = useI18nExtended()

const materialsExpanded = ref(false)

const visibleMaterials = (materials: string | string[]) => {
  const list = Array.isArray(materials) ? materials : [materials]
  if (materialsExpanded.value) {
    return list
  }
  return list.slice(0, 4)
}

const getColorClass = (rating: number): string => {
  switch (rating) {
  case 0: return 'rating-red'
  case 1: return 'rating-yellow'
  case 2: return 'rating-green'
  default: return ''
  }
}

const getColumnTitle = (fieldName: string) => {
  const column = props.tableColumns.find((c: any) => c.field === fieldName)
  return (column as any)?.title || ''
}

const hasMinField = (fieldName: string) => {
  const column = props.tableColumns.find((c: any) => c.field === fieldName)
  return (column as any)?.minField || false
}

const hasMeanField = (fieldName: string) => {
  const column = props.tableColumns.find((c: any) => c.field === fieldName)
  return (column as any)?.meanField || false
}

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  tableColumns: {
    type: Array,
    required: true,
    default: () => []
  },
  dataItems: {
    type: Array,
    required: true
  },
  isInchComputed: {
    type: String,
    required: false,
    default: 'mm'
  }
})
</script>

<style lang="scss">
.oring-assistant-upper-grid-wrapper {
  .grid-table {
    background-color: $color__fst__blue__010;
    border: 0;

    .k-grid-aria-root {
      margin-right: 1.0325rem;
    }

    .k-table-thead{
      background-color: $color__fst__blue__010;
    }

    .td-content {
      display: flex;
      justify-content: flex-end;

      &__unit-spacing {
        padding-left: .2rem;
      }
    }

    .td-content-stretching {
        display: flex;
        justify-content: flex-end;

        &__values {
          display: flex;
          flex-direction: column;
        }
      }

    .rating-red,
    .rating-yellow,
    .rating-green {
      display: flex;
      justify-content: flex-end;

      .value-label{
        padding-right: .2rem;
      }
    }

    .rating-red {
      color: $color__fst__error__contrast;
    }

    .rating-yellow {
      color: $color__fst__warning__contrast;
    }

    .rating-green {
      color: $color__fst__success__contrast;
    }

    .material-list {
      ul {
        list-style: none;
        margin: 0;
        padding: 0;

        li {
          padding: .125rem 0;
        }
      }

      &__toggle {
        display: flex;
        align-items: center;
        cursor: pointer;
        color: $color__fst__primary__hover;
        font-weight: $base-text-font-weight-light;
        font-size: .875rem;
        margin-top: .25rem;

        svg {
          width: 1.5rem;
        }

        .expand-more-icon {
          transform: rotate(0deg);
        }

        .expand-less-icon {
          transform: rotate(180deg);
        }
      }
    }
  }
}

@include breakpoint-down($sm) {
  .oring-assistant-upper-grid-wrapper {
    width: calc(100vw - 1rem);
    padding: 0;
    margin: 0;
    margin-left: calc(-50vw + 50%);
    margin-right: calc(-50vw + 50%);

    .k-grid {
      width: 100%;

      table {
        thead { display: none; }

        tr {
          display: flex;
          flex-direction: column;
          padding: 0 1rem;
          margin: 0.2rem 0 0 1.2rem;
          box-shadow: none;
          border-radius: 0.25rem;
          width: calc(100vw - 2.5rem);
          box-sizing: border-box;
        }

        td {
          font-weight: $base-text-font-weight-semi-bold;
          display: flex;
          align-items: center;
          padding: 0.5rem 0;
          border-bottom: 0.0625rem solid $color__fst__gray__030;
          position: relative;
          width: 100%;
          box-sizing: border-box;
          gap: 1rem;

          &[data-th]::before {
            content: attr(data-th);
            margin-right: 0.5rem;
            font-weight: $base-text-font-weight-light;
            text-transform: none;
            flex: 0 0 40%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .td-content,
          .td-content-stretching {
            justify-content: flex-start;
            flex: 1;
            text-align: left;
            min-width: 0;
            overflow: hidden;

            .value-label {
              font-weight: 600;
            }
          }

          .td-content-stretching__values {
            align-items: flex-start;
            text-align: left;
          }
        }

        td.empty-column {
          display: none;
        }

        tr > td:nth-last-child(2) {
          border-bottom: none;
          padding-bottom: 0;
        }

        tr > td:not(.empty-column):first-child,
        tr > td.empty-column + td {
          padding-top: 0;
        }
      }
    }
  }

}

</style>
